import toast from '@services/toasts';
import { fail, success } from '@helpers/result-helper';

export class VuexBuilder {
	#commit = null
	#loadingOperationName = ''
	#requestFn = null
	#toastSuccess = null
	#toastError = null
	#commitName = null
	#commitErrorName = null
	#commitData = null
	#transformFn = null
	#isDownload = null

	constructor(commit) {
		this.#commit = commit
	}

	/**
	 * This assumes the module contains a mutation 'START_OPERATION' and 'FINISH_OPERATION'
	 * @param operationKey - the key to the operations state object
	 * @returns {VuexBuilder}
	 */
	operationKey(operationKey) {
		this.#loadingOperationName = operationKey
		return this;
	}

	successToast(stringOrToastFn) {
		this.#toastSuccess = stringOrToastFn
		return this;
	}

	errorToast(stringOrToastFn) {
		this.#toastError = stringOrToastFn
		return this;
	}

	/**
	 * Pass the name of the commit function. This will pass back the request data
	 * on the successful completion if no data is passed as a second param
	 */
	commitData(commitName, data= null) {
		this.#commitName = commitName

		if (data) this.#commitData = data

		return this;
	}

	/**
	 * Use this to transform the data before it is commited
	 */
	transformData(transformFn) {
		this.#transformFn = transformFn
		return this;
	}


	commitError(errorCommitName) {
		this.#commitErrorName = errorCommitName
		return this;
	}


	request(requestFn)  {
		this.#requestFn = requestFn
		return this;
	}

	/**
	 * This will execute a download request. Note since this expects a file type there is no success commit.
	 * @returns {Promise<{data: null, message: string, errors: [], isSuccess: boolean, statusCode: null}>}
	 */
	async download(fileName) {
		if (this.#requestFn == null) {
			console.error("vuex builder has not been assigned a request function. This is required.");
			return;
		}
	
		try {
			if (this.#loadingOperationName)
				this.#commit('START_OPERATION', this.#loadingOperationName);
	
			const response = await this.#requestFn();
	
			if (!response || !response.data) {
				throw new Error("Download request returned an invalid response.");
			}
	
			const contentDisposition = response.headers['content-disposition'];
			let serverFileName = fileName;
	
			if (contentDisposition) {
				const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
				if (match && match[1]) {
					serverFileName = match[1].replace(/['"]/g, ''); // Remove quotes
				}
			}
	
			if (this.#toastSuccess) {
				if (typeof this.#toastSuccess === 'string') toast.success(this.#toastSuccess);
				else this.#toastSuccess();
			}
	
			const blob = new Blob([response.data]);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', serverFileName);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);
	
			return success();
		}
		catch (err) {
			console.error("Download error:", err);
	
			const errorData = (err.response && err.response.data) ? err.response.data : err.message;
	
			if (this.#commitErrorName)
				this.#commit(this.#commitErrorName, errorData);
	
			if (this.#toastError) {
				if (typeof this.#toastError === 'string') toast.error(this.#toastError);
				else this.#toastError(err);
			}
	
			return fail(errorData);
		}
		finally {
			if (this.#loadingOperationName)
				this.#commit('FINISH_OPERATION', this.#loadingOperationName);
		}
	}


	/**
	 * This will execute the request make appropriate commits and return the data or error
	 * @returns {Promise<{data: null, message: string, errors: [], isSuccess: boolean, statusCode: null}>}
	 */
	async execute() {
		if (this.#requestFn == null) console.error("vuex builder has not been assigned a request function. This is required.")

		try {
			if (this.#loadingOperationName)
				this.#commit('START_OPERATION', this.#loadingOperationName);

			const response = await this.#requestFn();

			const originalData = response.data
			if (this.#transformFn)
				response.data = this.#transformFn(response.data)

			if (this.#commitName)
				this.#commit(this.#commitName, this.#commitData ? this.#commitData : response.data)

			if (this.#toastSuccess) {
				if (typeof this.#toastSuccess === 'string')
					toast.success(this.#toastSuccess);
				else
					this.#toastSuccess()
			}

			if (this.#isDownload) {
				const url = window.URL.createObjectURL(new Blob([response.data]));
				const link = document.createElement('a');
				link.href = url;
				link.setAttribute('download', data.fileName);
				document.body.appendChild(link);
				link.click();
			}

			return success(originalData);

		}
		catch (err) {
			const errorData = err.response.data;

			if (this.#commitErrorName)
				this.#commit(this.#commitErrorName, errorData)

			if (this.#toastError) {
				if (typeof this.#toastError === 'string')
					toast.error(this.#toastError);
				else
					this.#toastError(err)
			}

			return fail(errorData);
		}
		finally {
			if (this.#loadingOperationName)
				this.#commit('FINISH_OPERATION', this.#loadingOperationName);
		}
	}

}